import { MEMBER_INVITE_STATUS } from "@/constants/board";
import { useMultiSelect } from "@/utils/hooks/useMultiSelect";
import { useClickAway, useMemoizedFn } from "ahooks";
import { SelectProps } from "antd";
import React, { useCallback, useRef, useState } from "react";
import HBAvatar from "../Avatar";
import { InviteAction } from "../BoardMember";
import HBIcon from "../HBIcon";
import HBSelect from "../HBInput/select";
import styles from "./index.module.less";
import { UserList } from "./list";

export interface User {
  user_id: number;
  username: string;
  urlname: string;
  avatar: {
    id: number;
    farm: string;
    bucket: string;
    key: string;
    type: string;
    width: number;
    height: number;
    frames: number;
  };
  pin_count?: number;
  follower_count?: number;
  board_count?: number;
  extra?: {
    is_museuser?: boolean;
    gd_id?: string;
  };
}

export interface UserSelectProps {
  /** 埋点来源 */
  source?: string;
  value?: number[];
  onChange?: (value: number[], users?: User | User[]) => void;
}

export const UserSelect: React.FC<
  UserSelectProps & SelectProps<number | number[]>
> = (props) => {
  const dropdownRenderRef = useRef(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const selectRef = useRef(null);

  useClickAway(() => {
    setOpen(false);
  }, [dropdownRenderRef, selectRef?.current?.wrap]);

  const { value, onToggleSelect, setValue } = useMultiSelect({
    ...props,
    compareKey: "user_id",
    maxCount: 20,
    maxMessage: "已达今日上限，可创建后通过链接邀请",
  } as UserSelectProps);

  const onSearch = useMemoizedFn((keyword) => {
    setSearchKeyword(keyword);
  });

  const actionRender = useMemoizedFn((user: IUser) => {
    return (
      <InviteAction
        user={user}
        onClick={() => onToggleSelect(user)}
        status={
          value.some((item) => item.user_id === user.user_id)
            ? MEMBER_INVITE_STATUS.INVITING
            : undefined
        }
      />
    );
  });

  const dropdownRender = useCallback(() => {
    if (!searchKeyword) {
      return (
        <div ref={dropdownRenderRef} className={styles.empty}>
          请输入用户名搜索
        </div>
      );
    }

    return (
      <div ref={dropdownRenderRef}>
        <UserList
          selecteds={value.map((item) => item.user_id)}
          searchKeyword={searchKeyword}
          onSelect={onToggleSelect}
          renderAction={actionRender}
          keyboardNavigation
          data-content-source={props.source}
        />
      </div>
    );
  }, [searchKeyword, onToggleSelect, value, actionRender]);

  const tagRender = useMemoizedFn((item) => {
    const user = value.find((u) => u.user_id === item.value);

    if (!user) {
      return;
    }

    return (
      <div className={styles.userTag} key={user.user_id}>
        <HBAvatar user={user} size={24} shape="circle" />
        <span className={styles.username}>{user.username}</span>
        <HBIcon type="ic_close" onClick={() => onToggleSelect(user)} />
      </div>
    );
  });

  return (
    <HBSelect
      {...props}
      // 直接使用value会导致选中的tag渲染时，key不唯一
      value={value.map((item) => item.user_id)}
      onChange={(values) => {
        setValue(values.map((item) => value.find((u) => u.user_id === item)));
      }}
      open={open}
      onDropdownVisibleChange={setOpen}
      dropdownRender={dropdownRender}
      onSearch={onSearch}
      mode={"multiple"}
      className={styles.select}
      tagRender={tagRender}
      fieldNames={{ value: "user_id" }}
      noWrapper
      maxTagCount={20}
      ref={selectRef}
    ></HBSelect>
  );
};
